// Single source of truth for timesheet-basic field labels. Used by:
// - the settings page itself (TimesheetBasicPageContent.tsx) to build diffLabels
// - the timesheet "Explain" modal so it can show the same labels users see in settings.
//
// Keys here are the camelCase field names from TimesheetConfigurationModelParsed.
// Values are translation keys (NOT translated strings) so the map can be imported
// without React/i18n context.

export const TIMESHEET_BASIC_TAB_SCOPE = 'timesheet-basic-settings'

export type TimesheetBasicTabId = 'a' | 'b' | 'c'

// Maps each field to the tab it lives on. Tabs:
// - 'a' = Basic
// - 'b' = Settlement parameters
// - 'c' = Settlement periods
export const TIMESHEET_BASIC_TAB_KEYS: Record<string, TimesheetBasicTabId> = {
  fitWorkingDays: 'a',
  useScheduleFromEmptyDays: 'a',
  recompenseOverhours: 'a',
  returnDuty100: 'a',
  overhoursAtTheRequest: 'a',
  recompenseWorkOnFreeDay: 'a',
  duty50ToWorkTime: 'a',
  setLeaveForWorkingDaysWithNoWork: 'a',
  addFreeDayForHolidayOnSaturday: 'a',
  calculateAllMonthsForPeriod: 'a',
  startDayFromSchedule: 'b',
  prepareScheduleOnCalculation: 'b',
  weekFreeDay: 'b',
  nightTimeStartHours: 'b',
  nightTimeStartMinutes: 'b',
  workTimeLimitIntervalStartTimeHours: 'b',
  workTimeLimitIntervalStartTimeMinutes: 'b',
  holidayStartTime: 'b',
  overhoursLimit: 'b',
  currentSettlementType: 'c',
  firstSettlementPeriodStartDate: 'c',
  settlementPeriods: 'c',
}

export const TIMESHEET_BASIC_LABEL_KEYS: Record<string, string> = {
  fitWorkingDays: 'configuration-timesheet.auto-work-days',
  useScheduleFromEmptyDays: 'configuration-timesheet.auto-schedule-empty-days',
  recompenseOverhours: 'configuration-timesheet.auto-recompense-overhours',
  returnDuty100: 'configuration-timesheet.auto-return-duty100',
  overhoursAtTheRequest: 'configuration-timesheet.auto-overhours-at-request',
  recompenseWorkOnFreeDay: 'configuration-timesheet.auto-recompense-free-day',
  duty50ToWorkTime: 'configuration-timesheet.auto-duty50-to-worktime',
  setLeaveForWorkingDaysWithNoWork: 'configuration-timesheet.auto-insert-leave-days',
  addFreeDayForHolidayOnSaturday: 'configuration-timesheet.auto-reduce-holiday-saturday',
  calculateAllMonthsForPeriod: 'configuration-timesheet.auto-calculate-all-months',
  startDayFromSchedule: 'configuration-timesheet.start-working-days',
  prepareScheduleOnCalculation: 'configuration-timesheet.adjust-days-equivalent-system',
  weekFreeDay: 'configuration-timesheet.additional-free-day',
  nightTimeStartHours: 'configuration-timesheet.night-work-kp-art151',
  nightTimeStartMinutes: 'configuration-timesheet.night-work-kp-art151',
  workTimeLimitIntervalStartTimeHours: 'configuration-timesheet.night-work-uocpk-art21',
  workTimeLimitIntervalStartTimeMinutes: 'configuration-timesheet.night-work-uocpk-art21',
  holidayStartTime: 'configuration-timesheet.holiday-start-time',
  overhoursLimit: 'configuration-timesheet.yearly-overhours-limit',
  currentSettlementType: 'configuration-timesheet.settlement-period-length',
  firstSettlementPeriodStartDate: 'configuration-timesheet.settlement-period-start-month',
  settlementPeriods: 'configuration-timesheet.tab-settlement-periods',
}

// "(minutes)" suffix is added in the diff view for hours/minutes pairs. Returns
// the suffix or empty string for a given field name.
const MINUTE_SUFFIX_FIELDS = new Set([
  'nightTimeStartMinutes',
  'workTimeLimitIntervalStartTimeMinutes',
])

export const buildTimesheetBasicDiffLabels = (
  t: (key: string) => string,
): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const [field, labelKey] of Object.entries(TIMESHEET_BASIC_LABEL_KEYS)) {
    out[field] = MINUTE_SUFFIX_FIELDS.has(field)
      ? `${t(labelKey)} (minuty)`
      : t(labelKey)
  }
  return out
}
